import { Buffer } from 'buffer'
import { crypto } from 'bitcoinjs-lib'
import { OFFER_COSIGNER_PUBLIC_KEY, OFFER_MARKET_FEE_ADDRESS } from './offer-constants'
import { normalizeOutpoint } from './item-listing'
import { OfferError } from './offer-errors'

/** Intent fields that describe where the item is right now: outpoint, value, owner. */
export const ITEM_LOCATION_FIELDS = [13, 14, 15] as const

/** Consensus encoding of an outpoint as the intent carries it: txid bytes reversed, vout LE. */
export function encodeIntentOutpoint(txid: string, index: number): string {
	const vout = Buffer.alloc(4)
	vout.writeUInt32LE(index)
	return Buffer.concat([Buffer.from(txid, 'hex').reverse(), vout]).toString('hex')
}

/**
 * Verify the intent the buyer is about to commit to in the escrow leaf. The
 * ordered fields mirror the API's offer_intent_fields! macro:
 *  0 protocol, 1 network, 2 offer id, 3 buyer x-only key, 4 co-signer key,
 *  5 payment key, 6 buyer address, 7 refund address, 8 scope, 9 collection,
 *  10 trait type, 11 trait value, 12 inscription id, 13 item outpoint,
 *  14 item value, 15 seller address, 16 NAT terms, 17 price, 18 market fee,
 *  19 network fee, 20 escrow value, 21 market fee address, 22 expiry,
 *  23 recovery delay, 24 message.
 * The co-signer key and the fee address are pinned in the client. For an
 * item offer the outpoint, value and owner (13-15) are required and must
 * match the live lookup the caller made just before; a mismatch there
 * throws OfferError('offer_item_moved') so the caller can look again.
 */
export function verifyOfferIntent(
	fields: unknown,
	commitment: string,
	expected: {
		id: string
		buyerKey: string
		paymentKey: string
		buyer: string
		refund: string
		scope: string
		collection: string | null
		traitType: string | null
		traitValue: string | null
		price: number
		marketFee: number
		networkFee: number
		escrow: number
		expiry: string
		delay: number
		message: string | null
		nat: unknown
		inscriptionId?: string
		/** Item scope (all three required): the live outpoint, output value and owner. */
		itemOutpoint?: string | null
		itemValue?: number | null
		sellerAddress?: string | null
	}
) {
	if (
		!Array.isArray(fields) ||
		fields.length !== 25 ||
		fields[0] !== 'ordinalswallet/funded-offer/v2' ||
		fields[1] !== 'bitcoin-mainnet'
	)
		throw new Error('Unsupported offer authorization. Nothing was funded.')
	if (fields[4] !== OFFER_COSIGNER_PUBLIC_KEY)
		throw new Error('The offers service named an unexpected co-signer key. Nothing was funded.')
	if (fields[21] !== OFFER_MARKET_FEE_ADDRESS)
		throw new Error('The offers service named an unexpected fee address. Nothing was funded.')
	const required: Record<number, unknown> = {
		2: expected.id,
		3: expected.buyerKey.slice(-64),
		5: expected.paymentKey,
		6: expected.buyer,
		7: expected.refund,
		8: expected.scope,
		9: expected.collection,
		10: expected.traitType,
		11: expected.traitValue,
		16: expected.nat,
		17: String(expected.price),
		18: String(expected.marketFee),
		19: String(expected.networkFee),
		20: String(expected.escrow),
		21: OFFER_MARKET_FEE_ADDRESS,
		22: String(Math.floor(Date.parse(expected.expiry) / 1000)),
		23: String(expected.delay),
		24: expected.message
	}
	if (expected.scope === 'item') {
		delete required[9]
		const match = expected.inscriptionId?.match(/^([0-9a-f]{64})i(\d+)$/i)
		if (!match || Number(match[2]) > 0xffffffff) throw new Error('Invalid item intent.')
		required[12] = encodeIntentOutpoint(match[1], Number(match[2]))
		// Fail closed: an item offer is only funded against a live lookup.
		const outpoint = normalizeOutpoint(expected.itemOutpoint)
		if (
			!outpoint ||
			expected.itemValue == null ||
			!Number.isSafeInteger(expected.itemValue) ||
			expected.itemValue <= 0 ||
			!expected.sellerAddress
		)
			throw new Error('The item’s current output could not be verified. Nothing was funded.')
		const [txid, vout] = outpoint.split(':')
		required[13] = encodeIntentOutpoint(txid, Number(vout))
		required[14] = String(expected.itemValue)
		required[15] = expected.sellerAddress
	} else {
		required[12] = ''
		required[13] = ''
		required[14] = '0'
		required[15] = ''
	}
	const differs = (index: number) =>
		JSON.stringify(fields[index]) !== JSON.stringify(required[index])
	const changed = Object.keys(required).map(Number).filter(differs)
	if (changed.length) {
		// Only the item's location moved: the caller may look again.
		if (expected.scope === 'item' && changed.every((i) => (ITEM_LOCATION_FIELDS as readonly number[]).includes(i)))
			throw new OfferError('offer_item_moved')
		throw new Error('Offer authorization terms changed. Nothing was funded.')
	}
	const hash = crypto.sha256(Buffer.from(JSON.stringify(fields))).toString('hex')
	if (hash !== commitment) throw new Error('Offer intent commitment mismatch. Nothing was funded.')
	return hash
}
